import type { ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import { CustomerSupportIcon } from "@hugeicons-pro/core-stroke-rounded";
import { Button, ButtonSize, ButtonVariant } from "shared/components";
import { IconAI } from "shared/components/ai/IconAI";
import { Show } from "shared/components/helpers/Show";
import type { BoomHelpHandoffReason } from "api/boom-help/types";
import {
  ANSWER_TEXT_CLASS,
  BOOM_HELP_COPY,
  HANDOFF_REASON_BY_KIND
} from "../constants";
import type { BoomHelpEntry } from "../store";
import { AnswerContent } from "./AnswerContent";
import { FeedbackControls } from "./FeedbackControls";

const AVATAR_SIZE = 24;

export const TalkToSupportButton = ({
  loading,
  onClick
}: {
  loading: boolean;
  onClick: () => void;
}) => (
  <Button
    variant={ButtonVariant.BASE}
    size={ButtonSize.SM}
    HugeIcon={CustomerSupportIcon}
    loading={loading}
    onClick={onClick}
  >
    {BOOM_HELP_COPY.talkToSupport}
  </Button>
);

export const WritingIndicator = () => (
  <div className="flex items-center gap-3">
    <IconAI bgSize={AVATAR_SIZE} />
    <span
      className="flex gap-1"
      role="status"
      aria-label={BOOM_HELP_COPY.writing}
    >
      {[0, 150, 300].map((delay) => (
        <span
          key={delay}
          className="size-1.5 animate-bounce rounded-full bg-content-200"
          style={{ animationDelay: `${delay}ms` }}
        />
      ))}
    </span>
  </div>
);

export const HelpRow = ({
  label,
  busy,
  children
}: {
  label: string;
  busy?: boolean;
  children: ReactNode;
}) => (
  <article aria-label={label} aria-busy={busy} className="flex gap-3">
    <span className="mt-0.5 shrink-0">
      <IconAI bgSize={AVATAR_SIZE} />
    </span>
    <div className="min-w-0 flex-1 space-y-3">{children}</div>
  </article>
);

export const MessageItem = ({
  message,
  onHandOff,
  onRetry,
  handingOff
}: {
  message: BoomHelpEntry;
  onHandOff: (reason: BoomHelpHandoffReason) => void;
  onRetry: () => void;
  handingOff: boolean;
}) => {
  if (message.role === "user")
    return (
      <article aria-label="You" className="flex justify-end">
        <p className="max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-tr-none border border-alpha-80 bg-primary-20 px-4 py-3 text-sm font-medium text-content-1000 shadow-xs [overflow-wrap:anywhere]">
          {message.content}
        </p>
      </article>
    );

  if (message.status === "pending") return <WritingIndicator />;

  const isStreaming = message.status === "streaming";
  const isFailed = message.status === "failed";
  const handoffReason =
    (message.kind && HANDOFF_REASON_BY_KIND[message.kind]) ?? "user_clicked";

  return (
    <HelpRow label={BOOM_HELP_COPY.title} busy={isStreaming}>
      <Show if={isStreaming}>
        <div className={ANSWER_TEXT_CLASS}>
          <ReactMarkdown>{message.content}</ReactMarkdown>
        </div>
      </Show>
      <Show if={isFailed}>
        <p className="text-sm font-medium text-content-700">
          {BOOM_HELP_COPY.failed}
        </p>
        <div className="flex">
          <Button
            variant={ButtonVariant.BASE}
            size={ButtonSize.SM}
            onClick={onRetry}
          >
            {BOOM_HELP_COPY.retry}
          </Button>
        </div>
      </Show>
      <Show if={message.status === "completed"}>
        <AnswerContent content={message.content} screens={message.screens} />
        <Show if={message.offer_handoff}>
          <div className="flex">
            <TalkToSupportButton
              loading={handingOff}
              onClick={() => onHandOff(handoffReason)}
            />
          </div>
        </Show>
        <FeedbackControls message={message} />
      </Show>
    </HelpRow>
  );
};
